/**
 * A picture that is still coming (roadmap D85).
 *
 * Every view that draws something from a host answer — the branch map,
 * Architecture, the timeline, a plan's graph, every repository's memory —
 * shows this until the first answer arrives, instead of an empty canvas:
 * a spinner and a faint skeleton of what is coming, a sentence saying what
 * is being read, how long it has waited once that is more than a moment,
 * and a way to ask again once the wait is clearly too long. A failure the
 * view knows about takes the same place, with the reason and Retry.
 *
 *   canvas  fills the frame the picture will fill (the default)
 *   inline  one line, for a list or a side panel
 *
 * The words and timings are `loadingStateModel.ts`.
 */
import './loadingState.css';

import { TriangleExclamation } from '@gravity-ui/icons';
import { Button, Skeleton, Spinner } from '@heroui/react';
import { useEffect, useState } from 'react';

import { FitText } from './FitText.tsx';
import { loadingLook } from './loadingStateModel.ts';

export interface LoadingStateProps {
  /** What is being read, as a sentence: "Reading the branches…". */
  label: string;
  /** Progress, when the work reports any: the engine's last line, "3 of 12". Shown under the label while waiting. */
  detail?: string;
  /** What went wrong, when the view knows. Shows the failure state. */
  error?: string | null;
  /** The failure's title: "The branch map could not be read". */
  failedTitle?: string;
  /** Ask again. Offered on a failure and once the wait is too long. */
  onRetry?: () => void;
  variant?: 'canvas' | 'inline';
  /** The faint picture behind the words, canvas only. */
  skeleton?: 'graph' | 'rows' | 'none';
  /** Take the parent's full height (a canvas frame with a fixed height). */
  fill?: boolean;
  className?: string;
}

const GRAPH_COLUMNS = [3, 2, 1];

function SkeletonPicture({ shape }: { shape: 'graph' | 'rows' }) {
  if (shape === 'rows') {
    return (
      <div className="loading-state__skeleton" data-shape="rows" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="loading-state__row" style={{ width: `${92 - ((i * 17) % 40)}%` }} />
        ))}
      </div>
    );
  }
  return (
    <div className="loading-state__skeleton" data-shape="graph" aria-hidden="true">
      {GRAPH_COLUMNS.map((cards, column) => (
        <div key={column} className="loading-state__column">
          {Array.from({ length: cards }, (_, i) => (
            <Skeleton key={i} className="loading-state__card" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function LoadingState({ label, detail, error, failedTitle, onRetry, variant = 'canvas', skeleton = 'graph', fill, className }: LoadingStateProps) {
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [now, setNow] = useState(startedAt);
  const failed = !!error;
  useEffect(() => {
    if (failed) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [failed, startedAt]);

  const look = loadingLook({ startedAt, now, error });
  const retry = onRetry
    ? () => {
        const at = Date.now();
        setStartedAt(at);
        setNow(at);
        onRetry();
      }
    : undefined;

  if (variant === 'inline') {
    return (
      <div className={`loading-inline ${className ?? ''}`} data-phase={look.phase} data-loading-state={look.phase} role={failed ? 'alert' : 'status'}>
        {failed ? <TriangleExclamation className="size-3.5" aria-hidden="true" /> : <Spinner size="sm" />}
        <span>{failed ? `${failedTitle ?? 'This could not be read'}: ${error}` : label}</span>
        {!failed && look.elapsed ? <span className="loading-state__elapsed">{look.elapsed}</span> : null}
        {!failed && detail ? <FitText className="loading-inline__detail" data-loading-detail>{detail}</FitText> : null}
        {retry && (failed || look.phase === 'slow') ? (
          <Button size="sm" variant="ghost" onPress={retry}>
            {failed ? 'Retry' : 'Ask again'}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`loading-state ${className ?? ''}`} data-fill={fill || undefined} data-loading-state={look.phase}>
      {!failed && skeleton !== 'none' ? <SkeletonPicture shape={skeleton} /> : null}
      {failed ? (
        <div className="loading-state__pill" role="alert">
          <span className="loading-state__icon">
            <TriangleExclamation aria-hidden="true" />
          </span>
          <span className="loading-state__title">{failedTitle ?? 'This could not be read'}</span>
          <span className="loading-state__detail">{error}</span>
          {retry ? (
            <Button size="sm" variant="secondary" onPress={retry} data-loading-retry>
              Retry
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="loading-state__pill" role="status" aria-live="polite">
          <span className="loading-state__line">
            <Spinner size="sm" />
            <span>{label}</span>
            {look.elapsed ? <span className="loading-state__elapsed">{look.elapsed}</span> : null}
          </span>
          {detail ? (
            <FitText className="loading-state__detail loading-state__progress" data-loading-detail>
              {detail}
            </FitText>
          ) : null}
          {look.phase === 'slow' ? (
            <>
              <span className="loading-state__detail">This is taking longer than usual. The server may be busy, or the connection may have dropped.</span>
              {retry ? (
                <Button size="sm" variant="ghost" onPress={retry} data-loading-retry>
                  Ask again
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
